import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonPage,
  IonContent,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonText,
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  IonSpinner,
  useIonRouter,
  useIonViewWillEnter,
  useIonViewWillLeave,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  IonNote,
  IonRefresherContent,
  IonAvatar,
} from '@ionic/react';
import {
  notifications,
  warning,
  chevronBack,
  trash,
  mail,
  mailOpen,
  people,
  cash,
  calendarOutline,
  person,
  documentText,
  chatbubbles,
  ellipsisVertical,
  receiptSharp,
  close
} from 'ionicons/icons';
import { doc, updateDoc, deleteDoc, writeBatch, collection, query, where, orderBy, startAfter, limit, getDocs, Timestamp } from 'firebase/firestore';
import styles from './AlertsPage.module.scss';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import PositionedRefresher from '../components/PositionedRefresher';
import SearchModal from '../components/SearchModal';
import BookingDetails from './bookingSystem/BookingDetails';
import { useAuth } from '../contexts';
import { localizeNotification, type NotificationI18n } from '../utils/notificationI18n';
import { auth, dbRsRoles } from '../auth/firebase';
import { useTranslation } from 'react-i18next';

interface Alert {
  id: string;

  category: 'booking' | 'payment' | 'team' | 'system' | 'contact' | 'form' | 'message';
  title: string;
  message: string;
  time: string;
  date: string;
  read?: boolean;
  createdAt?: string;
  rawCreatedAt?: Timestamp | string;
  relatedId?: string;
  avatar?: string;
  isCancelled?: boolean;
  i18n?: NotificationI18n;
}

function transformNotif(notif: ReturnType<typeof Object.assign>): Alert {
  const category = notif.category || 'system';
  const validCategory = ['booking', 'payment', 'team', 'system', 'contact', 'form', 'message'].includes(category)
    ? (category as Alert['category']) : 'system' as Alert['category'];

  const dateTimeStr = notif.createdAt
    ? typeof notif.createdAt === 'string'
      ? new Date(notif.createdAt).toLocaleString()
      : new Date(notif.createdAt.toDate()).toLocaleString()
    : 'Just now';
  const [datePart, timePart] = dateTimeStr.includes(',')
    ? dateTimeStr.split(',').map((s: string) => s.trim()) : [dateTimeStr, ''];
  return {
    id: notif.id, category: validCategory,
    title: notif.title || 'Notification', message: notif.message || '',
    date: datePart, time: timePart || dateTimeStr,
    read: notif.read || false,
    createdAt: typeof notif.createdAt === 'string' ? notif.createdAt : notif.createdAt?.toDate().toISOString(),
    rawCreatedAt: notif.createdAt,
    relatedId: notif.relatedId,
    avatar: notif.avatar,
    i18n: notif.i18n,
    isCancelled: notif.title && notif.title.includes('Cancelled')
  };
}

const AlertsPage: React.FC = () => {
  const { t: tr } = useTranslation();
  const { t, i18n } = useTranslation();
  const router = useIonRouter();
  const { notifications: firestoreNotifications, markNotificationsSeen, authState } = useAuth();
  const location = useLocation();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  // Lazy-initialized from whatever the context already holds at mount —
  // AuthContext's Firestore listener is typically already populated by the
  // time this page is navigated to (well after login), so waiting for a
  // *change* to firestoreNotifications (the render-phase-sync effect below)
  // never fired on first render: there was nothing to compare against yet,
  // since prevFirestoreNotifications was seeded from that same already-
  // current reference. That left `alerts` permanently empty until a brand
  // new Firestore snapshot happened to arrive while the page was open.
  const [alerts, setAlerts] = useState<Alert[]>(() => firestoreNotifications.map(transformNotif));
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [openBookingId, setOpenBookingId] = useState<string | undefined>(undefined);
  const moreButtonRef = useRef<HTMLIonButtonElement>(null);
  const moreMenuCardRef = useRef<HTMLIonCardElement>(null);

  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);

  // Refs for PositionedRefresher
  const titleCardElementRef = useRef<HTMLDivElement>(null);
  const contentElementRef = useRef<HTMLIonGridElement>(null);
  const wasAtTop = useRef(true);
  const [refresherDisabled, setRefresherDisabled] = useState(false);

  // Pagination refs
  const lastRawCreatedAtRef = useRef<Timestamp | string | null>(null);
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);

  const getNotificationsRoute = useCallback(() => {
    if (location.pathname.startsWith('/tabs/dashboard/owner/alerts')) {
      return '/tabs/dashboard/owner/notifications';
    }

    return '/tabs/notifications';
  }, [location.pathname]);

  // When context notifications change (real-time top-50), merge into list.
  // New items at top replace their old versions; older loaded items stay
  // below. Computed during render (React's "adjust state when a dependency
  // changes" pattern) rather than as setState calls inside an effect, since
  // this is a pure synchronous transform of a context value.
  const [prevFirestoreNotifications, setPrevFirestoreNotifications] = useState(firestoreNotifications);
  if (prevFirestoreNotifications !== firestoreNotifications) {
    setPrevFirestoreNotifications(firestoreNotifications);
    const transformed = firestoreNotifications.map(transformNotif);
    const newIds = new Set(transformed.map(a => a.id));
    setAlerts(prev => [
      ...transformed,
      ...prev.filter(a => !newIds.has(a.id))
    ]);
  }

  // Refs are non-render-visible pagination bookkeeping, so they're kept in
  // an effect (ref writes during render are unsafe, unlike the setAlerts
  // derivation above).
  useEffect(() => {
    if (firestoreNotifications.length > 0) {
      const last = firestoreNotifications[firestoreNotifications.length - 1];
      lastRawCreatedAtRef.current = last.createdAt as Timestamp | string;
    }
    const more = firestoreNotifications.length === 50;
    hasMoreRef.current = more;
  }, [firestoreNotifications]);

  // Opening the alerts clears the "new" badge, and keeps it clear while the page
  // is open and more arrive.
  // Only while this page is actually showing: Ionic keeps visited pages mounted
  // behind the current one, and a hidden page must not clear the badge.
  const viewActiveRef = useRef(false);
  useIonViewWillEnter(() => {
    viewActiveRef.current = true;
    markNotificationsSeen();
  });
  useIonViewWillLeave(() => {
    viewActiveRef.current = false;
  });
  useEffect(() => {
    if (viewActiveRef.current) markNotificationsSeen();
  }, [firestoreNotifications, markNotificationsSeen]);

  useEffect(() => {
    if (!showOptionsMenu) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as Node;
      if (moreButtonRef.current?.contains(target)) return;
      if (moreMenuCardRef.current?.contains(target)) return;
      setShowOptionsMenu(false);
    };

    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [showOptionsMenu]);

  const loadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMoreRef.current) return;
    const uid = auth.currentUser?.uid;
    if (!uid || !lastRawCreatedAtRef.current) return;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const q = query(
        collection(dbRsRoles, 'notifications'),
        where('recipientId', '==', uid),
        orderBy('createdAt', 'desc'),
        startAfter(lastRawCreatedAtRef.current),
        limit(50)
      );
      const snap = await getDocs(q);
      const more = snap.docs.length === 50;
      const newAlerts = snap.docs.map(d => transformNotif({ id: d.id, ...d.data() }));

      if (snap.docs.length > 0) {
        const lastData = snap.docs[snap.docs.length - 1].data();
        lastRawCreatedAtRef.current = lastData.createdAt as Timestamp | string;
      }

      setAlerts(prev => {
        const existingIds = new Set(prev.map(a => a.id));
        return [...prev, ...newAlerts.filter(a => !existingIds.has(a.id))];
      });
      hasMoreRef.current = more;
    } catch (e) {
      console.error('Error loading more notifications:', e);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, []);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const snapHandler = useScrollSnap(contentElementRef, 66, applyProgress);

  const handleClose = () => {
    if (router.canGoBack()) {
      router.goBack();
    } else {
      router.push('/tabs/manage');
    }
  };

  const handleMarkAsRead = async (id: string) => {
    setAlerts(alerts.map(alert =>
      alert.id === id ? { ...alert, read: true } : alert
    ));
    try {
      await updateDoc(doc(dbRsRoles, 'notifications', id), { read: true });
    } catch (e) {
      console.error('Error marking as read:', e);
    }
  };

  // Tapping an alert goes to the thing it is about: a booking opens its details
  // modal here; a contact opens its page.
  const handleOpenAlert = (alert: Alert) => {
    if (!alert.relatedId) return;
    if (alert.category === 'booking') {
      setOpenBookingId(alert.relatedId);
    } else if (alert.category === 'contact') {
      router.push(`/tabs/contact/${alert.relatedId}`);
    } else if (alert.category === 'form' || alert.category === 'message') {
      router.push(`/tabs/inbox/${alert.relatedId}`);
    } else if (alert.category === 'payment') {
      // Invoice pages are owner/management only; other dashboards just read the alert.
      if (!['owner', 'salesManager', 'financeManager', 'manager'].includes(String(authState.user?.role))) return;
      router.push(`/tabs/invoice/${alert.relatedId}`);
    }
  };

  const handleDelete = async (id: string) => {
    const removed = alerts.find(alert => alert.id === id);
    setAlerts(prev => prev.filter(alert => alert.id !== id));
    try {
      await deleteDoc(doc(dbRsRoles, 'notifications', id));
    } catch (e) {
      console.error('Error deleting notification:', e);
      // Put it back so the list matches Firestore.
      if (removed) setAlerts(prev => [removed, ...prev.filter(a => a.id !== id)]);
    }
  };

  const handleMarkAllAsRead = async () => {
    const unread = alerts.filter(a => !a.read);
    setAlerts(alerts.map(alert => ({ ...alert, read: true })));
    if (unread.length > 0) {
      try {
        const batch = writeBatch(dbRsRoles);
        unread.forEach(alert => {
          batch.update(doc(dbRsRoles, 'notifications', alert.id), { read: true });
        });
        await batch.commit();
      } catch (e) {
        console.error('Error marking all as read:', e);
      }
    }
  };



  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'booking': return calendarOutline;
      case 'payment': return cash;
      case 'team': return people;
      case 'contact': return person;
      case 'form': return documentText;
      case 'message': return chatbubbles;
      default: return notifications;
    }
  };

  // Counts of the unread alerts that are loaded on this page (the first 50, plus
  // whatever has been scrolled in) — not a separate query over every unread one.
  const unreadAlerts = alerts.filter(a => !a.read);
  const alertStats = {
    total: alerts.length,
    unread: unreadAlerts.length,
    booking: unreadAlerts.filter(a => a.category === 'booking').length,
    payment: unreadAlerts.filter(a => a.category === 'payment').length,
    team: unreadAlerts.filter(a => a.category === 'team').length,
    form: unreadAlerts.filter(a => a.category === 'form').length,
    overdue: unreadAlerts.filter(a => (a.message + ' ' + a.title).toLowerCase().includes('overdue')).length,
    autoInvoice: unreadAlerts.filter(a => {
      const text = a.message.toLowerCase();
      return text.includes('invoice sent') || text.includes('automatic invoice');
    }).length,
  };

  return (
    <IonPage className={styles.page}>
      <BasePageWithHeader
        ref={baseHeaderRef}
        title={t('alerts.title')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <IonButton onClick={handleClose} shape='round' className={styles.backButton} aria-label={t('alerts.close')}>
            <IonIcon icon={chevronBack} />
          </IonButton>
        }
        endButtons={
          <IonButton
            ref={moreButtonRef}
            shape='round'
            className={styles.optionsButton}
            onClick={() => setShowOptionsMenu(prev => !prev)}
          >
            <IonIcon icon={ellipsisVertical} />
          </IonButton>
        }
      >
        <IonContent scrollY={false} className={styles.pageContent}>
          <PositionedRefresher
            titleRef={titleCardElementRef}
            contentRef={contentElementRef}
            offset={0}
            anchor="bottom"
            id="alerts-refresher"
            pullMin={120}
            pullMax={200}
            pullFactor={0.6}
            disabled={refresherDisabled}
            onIonRefresh={(e) => {
              e.detail.complete();
            }}
            style={{
              '--ion-color-step-250': 'var(--ion-background-color)',
              '--ion-color-step-200': 'var(--ion-background-color)',
              '--ion-background-color': 'transparent',
            } as React.CSSProperties}
          >
            <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
          </PositionedRefresher>
          <IonGrid
            ref={contentElementRef}
            className={styles.alertsContainer}
            onScroll={(e) => {
              const el = e.target as HTMLElement;
              const top = el.scrollTop || 0;
              const atTop = top <= 5;
              if (atTop !== wasAtTop.current) {
                wasAtTop.current = atTop;
                setRefresherDisabled(!atTop);
              }
              const distanceFromBottom = el.scrollHeight - top - el.clientHeight;
              if (distanceFromBottom < 200 && hasMoreRef.current && !isLoadingMoreRef.current) {
                loadMore();
              }
              snapHandler(top);
            }}
          >
            <div ref={titleCardElementRef}>
              <TitleCard
                ref={titleCardAnimationRef}
                title={t('alerts.title')}
                subtitle={t('alerts.subtitle')}
                subtitleClassName={styles.heroSubtitle}
                cardClassName={styles.alertsOverviewCard}
                titleContainerClassName={styles.titleContainer}
                titleClassName={styles.alertsTitle}
              />

              <IonCard className={styles.overviewCard}>
                <IonCardContent className={styles.overviewSection}>
                  <IonLabel className={styles.overviewTitle}>{t('alerts.overview')}</IonLabel>
                  <IonGrid className={styles.alertsStatsRow}>
                    <IonRow>
                      <IonCol size="4" className={styles.alertStatCol}>
                        <IonIcon icon={calendarOutline} className={`${styles.alertStatIcon} ${styles.alertStatIconPrimary}`} />
                        <IonText><p className={styles.alertStatValue}>{alertStats.booking}</p></IonText>
                        <IonText><p className={styles.alertStatMeta}>{t('alerts.bookings')}</p></IonText>
                      </IonCol>
                      <IonCol size="4" className={styles.alertStatCol}>
                        <IonIcon icon={cash} className={`${styles.alertStatIcon} ${styles.alertStatIconSuccess}`} />
                        <IonText><p className={styles.alertStatValue}>{alertStats.payment}</p></IonText>
                        <IonText><p className={styles.alertStatMeta}>{t('alerts.payments')}</p></IonText>
                      </IonCol>
                      <IonCol size="4" className={`${styles.alertStatCol} ${styles.alertStatColLast}`}>
                        <IonIcon icon={people} className={`${styles.alertStatIcon} ${styles.alertStatIconSecondary}`} />
                        <IonText><p className={styles.alertStatValue}>{alertStats.team}</p></IonText>
                        <IonText><p className={styles.alertStatMeta}>{t('alerts.team')}</p></IonText>
                      </IonCol>
                    </IonRow>
                  </IonGrid>

                  <IonGrid className={`${styles.alertsStatsRow} ${styles.alertsStatsRowSecondary}`}>
                    <IonRow>
                      <IonCol size="4" className={styles.alertStatCol}>
                        <IonIcon icon={documentText} className={`${styles.alertStatIcon} ${styles.alertStatIconMedium}`} />
                        <IonText><p className={styles.alertStatValue}>{alertStats.form}</p></IonText>
                        <IonText><p className={styles.alertStatMeta}>{t('alerts.forms')}</p></IonText>
                      </IonCol>
                      <IonCol size="4" className={styles.alertStatCol}>
                        <IonIcon icon={warning} className={`${styles.alertStatIcon} ${styles.alertStatIconWarning}`} />
                        <IonText><p className={styles.alertStatValue}>{alertStats.overdue}</p></IonText>
                        <IonText><p className={styles.alertStatMeta}>{t('alerts.overdue')}</p></IonText>
                      </IonCol>
                      <IonCol size="4" className={`${styles.alertStatCol} ${styles.alertStatColLast}`}>
                        <IonIcon icon={receiptSharp} className={`${styles.alertStatIcon} ${styles.alertStatIconTertiary}`} />
                        <IonText><p className={styles.alertStatValue}>{alertStats.autoInvoice}</p></IonText>
                        <IonText><p className={styles.alertStatMeta}>{t('alerts.autoInvoice')}</p></IonText>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonCardContent>
              </IonCard>
            </div>

            {/* Notifications List */}
            <IonList className={styles.cardsContainer} lines="none">
              {alerts.map((alert) => (
                <IonItemSliding key={alert.id} className={styles.alertSlidingItem}>
                  <IonItem
                    button
                    detail={false}
                    className={`${styles.alertItem} ${!alert.read ? styles.unread : ''}`}
                    onClick={() => {
                      handleMarkAsRead(alert.id);
                      handleOpenAlert(alert);
                    }}
                  >
                    <div
                      className={`${styles.notificationAvatar} ${alert.category === 'booking' ? styles.avatarBooking :
                          alert.category === 'payment' ? styles.avatarPayment :
                            alert.category === 'team' ? styles.avatarTeam :
                              alert.category === 'form' ? styles.avatarForm :
                                styles.avatarSystem
                        }`}
                      slot="start"
                    >
                      {alert.avatar ? (
                        <>
                          <IonAvatar className={styles.avatarImage}>
                            <img src={alert.avatar} alt={tr('pages.AlertsPage.notification')} />
                          </IonAvatar>
                          {alert.isCancelled && (
                            <IonIcon
                              icon={close}
                              color="danger"
                              className={styles.cancelledOverlay}
                            />
                          )}
                        </>
                      ) : (
                        <IonIcon
                          icon={getCategoryIcon(alert.category)}
                          className={styles.notificationIcon}
                        />
                      )}
                      {!alert.read && <div className={styles.unreadDot} />}
                    </div>
                    <IonLabel className={styles.alertLabel}>
                      <h3 className={styles.alertTitle}>{localizeNotification(t, i18n.language, alert).title.replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FAFF}]/gu, '').trim()}</h3>
                      <p className={styles.alertMessage}>{localizeNotification(t, i18n.language, alert).message.replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FAFF}]/gu, '').trim()}</p>
                    </IonLabel>
                    <IonNote slot="end" className={styles.alertTimeContainer}>
                      <IonText className={styles.alertTime}>{alert.createdAt ? new Date(alert.createdAt).toLocaleDateString(i18n.language) : alert.date}</IonText>
                      <IonText className={styles.alertTime}>{alert.createdAt ? new Date(alert.createdAt).toLocaleTimeString(i18n.language) : alert.time}</IonText>
                    </IonNote>
                  </IonItem>

                  <IonItemOptions side="end">
                    <IonItemOption color="primary" onClick={() => handleMarkAsRead(alert.id)}>
                      <IonIcon icon={alert.read ? mailOpen : mail} />
                    </IonItemOption>
                    <IonItemOption color="danger" onClick={() => handleDelete(alert.id)}>
                      <IonIcon icon={trash} />
                    </IonItemOption>
                  </IonItemOptions>
                </IonItemSliding>
              ))}
            </IonList>

            {isLoadingMore && (
              <div className={styles.loadMoreSpinner}>
                <IonSpinner name="bubbles" color="primary" />
              </div>
            )}
          </IonGrid>
        </IonContent>
      </BasePageWithHeader>

      {openBookingId && (
        <BookingDetails
          bookingId={openBookingId}
          isOpen={!!openBookingId}
          onClose={() => setOpenBookingId(undefined)}
        />
      )}

      <SearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}

      />
      {showOptionsMenu && (
        <IonCard ref={moreMenuCardRef} className={styles.alertsOptionsMenu}>
          <IonList lines="none" className={styles.alertsOptionsList}>
            <IonItem
              button
              detail={false}
              lines="none"
              className={styles.alertsOptionsItem}
              onClick={() => {
                setShowOptionsMenu(false);
                handleMarkAllAsRead();
              }}
            >
              <IonIcon icon={mailOpen} slot="start" className={styles.alertsOptionsIcon} />
              <IonLabel>{t('alerts.markAllRead')}</IonLabel>
            </IonItem>
            <IonItem
              button
              detail={false}
              lines="none"
              className={styles.alertsOptionsItem}
              onClick={() => {
                setShowOptionsMenu(false);
                router.push(getNotificationsRoute());
              }}
            >
              <IonIcon icon={notifications} slot="start" className={styles.alertsOptionsIcon} />
              <IonLabel>{t('alerts.manage')}</IonLabel>
            </IonItem>
          </IonList>
        </IonCard>
      )}
    </IonPage>
  );
};

export default AlertsPage;
